import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Store as StoreIcon, Plus, Trash2, Save, MapPin } from "lucide-react";

const emptyForm = { name: "", city: "", code: "", assigned_states: [] };

function StatesPicker({ states, selected, onChange, testIdPrefix = "state" }) {
  const allSelected = states.length > 0 && selected.length === states.length;
  const toggleAll = () => onChange(allSelected ? [] : states.slice());
  const toggle = (s) => {
    if (selected.includes(s)) onChange(selected.filter((x) => x !== s));
    else onChange([...selected, s]);
  };
  if (!states.length) return <div className="text-xs text-[#71717A]">Loading states…</div>;
  return (
    <div className="border border-[#E5E5E5] rounded-md">
      <div className="p-2 border-b border-[#E5E5E5] flex items-center justify-between bg-[#F9FAFB]">
        <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
          <Checkbox
            checked={allSelected}
            onCheckedChange={toggleAll}
            data-testid={`${testIdPrefix}-select-all`}
          />
          <span>Select all ({states.length})</span>
        </label>
        <span className="text-xs text-[#71717A]">{selected.length} selected</span>
      </div>
      <div className="max-h-56 overflow-auto p-2 grid grid-cols-2 gap-1">
        {states.map((s) => (
          <label key={s} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-[#F4F4F5] px-1 py-0.5 rounded">
            <Checkbox
              checked={selected.includes(s)}
              onCheckedChange={() => toggle(s)}
              data-testid={`${testIdPrefix}-${s.replace(/\s+/g, "-")}`}
            />
            <span>{s}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function StoresPage() {
  const [rows, setRows] = useState([]);
  const [states, setStates] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/stores");
    setRows(r.data);
  }, []);
  useEffect(() => {
    load();
    api.get("/meta/states").then((r) => setStates(r.data));
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/stores", form);
      toast.success("Store added");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add store");
    }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/stores/${editing.id}`, {
        name: editing.name, city: editing.city || "", code: editing.code || "",
        assigned_states: editing.assigned_states || [],
      });
      toast.success("Store updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this store? Sales agents linked to it will lose their store mapping.")) return;
    try {
      await api.delete(`/stores/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="data-label mb-1">Retail network</div>
          <h1 className="font-heading font-black text-4xl tracking-tight">Stores</h1>
          <p className="text-sm text-[#71717A] mt-1">
            Physical or partner stores where sales agents operate. Each store can serve one or more states — pick individually or select all.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#4F46E5] hover:bg-[#4338CA] text-white" data-testid="add-store-button">
              <Plus className="w-4 h-4 mr-1" /> Add store
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add store</DialogTitle>
              <DialogDescription>Create a new physical store. Sales agents can be linked to a store from Users &amp; Teams.</DialogDescription>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div>
                <Label className="data-label mb-1.5 block">Store name</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="store-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="data-label mb-1.5 block">City</Label>
                  <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="store-city-input" />
                </div>
                <div>
                  <Label className="data-label mb-1.5 block">Store code</Label>
                  <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. HEM-BLR-01" data-testid="store-code-input" />
                </div>
              </div>
              <div>
                <Label className="data-label mb-1.5 block flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> States this store serves
                </Label>
                <StatesPicker
                  states={states}
                  selected={form.assigned_states}
                  onChange={(v) => setForm({ ...form, assigned_states: v })}
                  testIdPrefix="store-state"
                />
              </div>
              <Button type="submit" className="bg-[#4F46E5] hover:bg-[#4338CA] text-white w-full" data-testid="store-submit">
                Create store
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-shell overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr><th>Name</th><th>City</th><th>Code</th><th>Serves states</th><th></th></tr>
            </thead>
            <tbody data-testid="stores-tbody">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-[#71717A] text-sm">
                    <StoreIcon className="w-8 h-8 mx-auto mb-2 text-[#A1A1AA]" />
                    No stores yet. Add your first store to start onboarding sales agents.
                  </td>
                </tr>
              ) : rows.map((s) => (
                <tr key={s.id} data-testid={`store-row-${s.id}`}>
                  <td className="font-medium">{s.name}</td>
                  <td className="text-sm text-[#52525B]">{s.city || "—"}</td>
                  <td className="font-mono text-xs">{s.code || "—"}</td>
                  <td className="text-xs text-[#52525B] max-w-[280px]">
                    {(s.assigned_states || []).length === 0
                      ? <span className="text-[#A1A1AA]">none</span>
                      : (s.assigned_states.length > 4
                        ? `${s.assigned_states.slice(0, 4).join(", ")} +${s.assigned_states.length - 4} more`
                        : s.assigned_states.join(", "))}
                  </td>
                  <td className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditing({ ...s, assigned_states: s.assigned_states || [] })} data-testid={`edit-store-${s.id}`}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => del(s.id)} data-testid={`delete-store-${s.id}`}>
                      <Trash2 className="w-4 h-4 text-[#DC2626]" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit store</DialogTitle>
            <DialogDescription>Update store details and served states.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label className="data-label mb-1.5 block">Name</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="data-label mb-1.5 block">City</Label>
                  <Input value={editing.city || ""} onChange={(e) => setEditing({ ...editing, city: e.target.value })} />
                </div>
                <div>
                  <Label className="data-label mb-1.5 block">Code</Label>
                  <Input value={editing.code || ""} onChange={(e) => setEditing({ ...editing, code: e.target.value })} />
                </div>
              </div>
              <div>
                <Label className="data-label mb-1.5 block flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> States this store serves
                </Label>
                <StatesPicker
                  states={states}
                  selected={editing.assigned_states || []}
                  onChange={(v) => setEditing({ ...editing, assigned_states: v })}
                  testIdPrefix="edit-store-state"
                />
              </div>
              <Button onClick={saveEdit} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white w-full" data-testid="save-store-edit">
                <Save className="w-4 h-4 mr-1" /> Save changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
